import { useEffect, useRef, useState } from "react";

function useInView(threshold = 0.15) {
  const ref = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setInView(true); }, { threshold });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [threshold]);
  return { ref, inView };
}

const bonos = [
  {
    num: "1",
    emoji: "🎁",
    title: "Mentalidad de Estratega",
    desc: "Piensa como dueño de negocio, no como autoempleado. El PDF que cambia cómo ves cada decisión financiera.",
    badge: "PDF Exclusivo",
    color: "#00FF88",
    borderColor: "#00FF88",
    bgColor: "rgba(0,255,136,0.05)",
  },
  {
    num: "2",
    emoji: "🎁",
    title: "Sella tu Embudo Financiero",
    desc: "Identifica gastos vampiro y redirige ese dinero a campañas de tráfico. Tu rentabilidad al siguiente nivel.",
    badge: "PDF Exclusivo",
    color: "#FF6B35",
    borderColor: "#FF6B35",
    bgColor: "rgba(255,107,53,0.05)",
  },
];

export default function BonosSection() {
  const { ref, inView } = useInView(0.1);

  return (
    <section
      id="bonos"
      ref={ref}
      className="relative bg-[#0a0a0a] py-20 md:py-28 px-6 overflow-hidden"
      style={{ fontFamily: "'Montserrat', sans-serif" }}
    >
      {/* Diagonal stripes bg */}
      <div
        className="absolute inset-0 opacity-5"
        style={{
          backgroundImage: "repeating-linear-gradient(-45deg, #FF6B35 0px, #FF6B35 1px, transparent 1px, transparent 20px)",
        }}
      />
      {/* Animated top/bottom border */}
      <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-[#FF6B35] via-[#FF3D00] to-[#FF6B35]" style={{ animation: "border-slide 3s linear infinite" }} />
      <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-[#FF6B35] via-[#FF3D00] to-[#FF6B35]" />

      <div className="relative max-w-5xl mx-auto">
        {/* Urgency banner */}
        <div
          className={`transition-all duration-700 ${inView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
        >
          <div className="bg-gradient-to-r from-[#FF6B35] to-[#FF3D00] rounded-2xl p-5 md:p-8 text-center mb-12 md:mb-16">
            <p className="text-black font-black text-xl sm:text-2xl md:text-4xl leading-tight">
              ⚠️ ATENCIÓN: BONOS EXCLUSIVOS SOLO POR HOY
            </p>
            <p className="text-black/80 mt-2 text-sm sm:text-base font-semibold">
              Si tomas acción HOY, te llevas 2 PDFs de mi arsenal privado
            </p>
          </div>
        </div>

        {/* Bonos grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16">
          {bonos.map((bono, i) => (
            <div
              key={bono.num}
              className={`relative rounded-2xl p-8 border transition-all duration-700 hover:scale-[1.02] ${
                inView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"
              }`}
              style={{
                background: bono.bgColor,
                borderColor: bono.borderColor,
                borderWidth: "2px",
                transitionDelay: `${(i + 1) * 150}ms`,
              }}
            >
              {/* Badge */}
              <span
                className="absolute top-4 right-4 text-xs font-bold px-3 py-1 rounded-full"
                style={{ background: `${bono.color}22`, color: bono.color }}
              >
                {bono.badge}
              </span>

              {/* Emoji */}
              <div className="text-5xl mb-5">{bono.emoji}</div>

              {/* Title */}
              <p className="text-xs font-bold uppercase tracking-widest mb-2" style={{ color: bono.color }}>
                Bono {bono.num}
              </p>
              <h3 className="text-white text-xl md:text-2xl font-black mb-4">"{bono.title}"</h3>
              <div className="w-12 h-0.5 rounded-full mb-4" style={{ background: bono.color }} />
              <p className="text-gray-400 text-sm leading-relaxed" style={{ fontFamily: "'Inter', sans-serif" }}>
                {bono.desc}
              </p>
            </div>
          ))}
        </div>

        {/* Valor total */}
        <div
          className={`bg-[#111] border border-white/10 rounded-2xl p-8 text-center mb-12 transition-all duration-700 delay-300 ${
            inView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
          }`}
        >
          <p className="text-gray-400 text-sm mb-3">Valor total del pack</p>
          <div className="flex flex-wrap items-center justify-center gap-4 md:gap-6 mt-3">
            <div className="text-center">
              <p className="text-gray-500 text-xs uppercase tracking-wider">Stock Simple</p>
              <p className="text-white text-xl md:text-2xl font-black line-through opacity-50">S/ 30</p>
            </div>
            <div className="text-[#00FF88] text-2xl md:text-3xl font-black">+</div>
            <div className="text-center">
              <p className="text-gray-500 text-xs uppercase tracking-wider">Budgettrack</p>
              <p className="text-white text-xl md:text-2xl font-black line-through opacity-50">S/ 30</p>
            </div>
            <div className="text-[#FF6B35] text-2xl md:text-3xl font-black">+</div>
            <div className="text-center">
              <p className="text-gray-500 text-xs uppercase tracking-wider">2 PDFs Bonos</p>
              <p className="text-white text-xl md:text-2xl font-black line-through opacity-50">S/ 20</p>
            </div>
            <div className="text-white text-2xl md:text-3xl font-black">=</div>
            <div className="text-center">
              <p className="text-[#00FF88] text-xs uppercase tracking-wider font-bold">HOY PAGAS</p>
              <p className="text-[#00FF88] text-3xl md:text-4xl font-black">S/ 25</p>
              <p className="text-gray-500 text-xs">/año</p>
            </div>
          </div>
        </div>

        {/* CTA 2 */}
        <div
          className={`text-center transition-all duration-700 delay-500 ${
            inView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
          }`}
        >
          <a
            href="#"
            className="inline-flex items-center justify-center gap-3 bg-gradient-to-r from-[#FF6B35] via-[#FF5520] to-[#FF3D00] text-white font-black text-lg md:text-xl px-10 py-6 rounded-2xl hover:scale-105 transition-all duration-300 cursor-pointer text-center w-full sm:w-auto"
            style={{ boxShadow: "0 0 40px rgba(255,107,53,0.4), 0 10px 40px rgba(255,61,0,0.3)", animation: "pulse-cta 2s infinite" }}
          >
            <span className="flex-shrink-0">🔥</span>
            <span className="leading-snug">
              QUIERO EL PACK + LOS BONOS<br className="block" />
              (S/ 25/AÑO)
            </span>
          </a>
          <p className="text-gray-500 text-xs mt-4">
            Acceso inmediato tras el pago · Garantía de satisfacción · 100% seguro
          </p>
        </div>
      </div>

      <style>{`
        @keyframes border-slide {
          0% { background-position: 0% 0%; }
          100% { background-position: 100% 0%; }
        }
        @keyframes pulse-cta {
          0%, 100% { box-shadow: 0 0 30px rgba(255,107,53,0.4), 0 10px 30px rgba(255,61,0,0.3); }
          50% { box-shadow: 0 0 60px rgba(255,107,53,0.7), 0 10px 50px rgba(255,61,0,0.5); }
        }
      `}</style>
    </section>
  );
}
